import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { MobileLayout } from "../components/layout/MobileLayout.jsx";

import { Input } from "../components/ui/Input.jsx";
import { Label } from "../components/ui/Label.jsx";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "../components/ui/Avatar.jsx";
import { Button } from "../components/ui/Button.jsx";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/Card.jsx";
import {
  ArrowLeft,
  Mail,
  Phone,
  Trash,
} from "lucide-react";
import { fetchUsersByIds, fetchUsersByRole } from "../services/teamService.js";
import { useToast } from "../hooks/useToast.js";
import { EditTeamMemberModal } from "../components/modals/EditTeamMemberModal.jsx";
import { DeleteTeamMemberModal } from "../components/modals/DeleteTeamMemberModal.jsx";

export default function TeamDetails() {
  const { memberId } = useParams();
  const navigate = useNavigate();

  const [member, setMember] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editOnOpen, setEditOnOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [creatorName, setCreatorName] = useState("");
  const { toast } = useToast();
  useEffect(() => {
    const loadCreatorName = async () => {
      if (member?.created_by) {
        try {
          const users = await fetchUsersByIds([member.created_by]);
          if (users?.length) {
            setCreatorName(users[0].name || "Unknown");
          } else {
            setCreatorName("Unknown");
          }
        } catch (err) {
         
          toast({
            variant: "destructive",
            title: "Error fetching creator name:",
          });
          setCreatorName("Unknown");
        }
      }
    };
    loadCreatorName();
  }, [member?.created_by]);
  useEffect(() => {
    const loadMember = async () => {
      setLoading(true);
      setError("");

      try {
        const res = await fetchUsersByRole("sales_person", {
          page: 1,
          limit: 50,
        });
        const users = res?.users || res?.data || [];

        const currentMember = users.find((u) => u.id === memberId);
        if (!currentMember) throw new Error("Team member not found");
        setMember(currentMember);
      } catch (err) {
        setError(err.message || "Failed to load team member");
      } finally {
        setLoading(false);
      }
    };

    loadMember();
  }, [memberId]);

  const handleCall = (e, phone) => {
    e.stopPropagation();
    if (!phone) return;
    const sanitized = phone.replace(/\D/g, "");
    window.location.href = `tel:${sanitized}`;
  };

  const handleEmail = (e, email) => {
    e.stopPropagation();
    if (!email) return;
    window.location.href = `mailto:${email}`;
  };

  if (loading) {
    return (
      <MobileLayout showNavigation={false}>
        <div className="p-4">Loading team member...</div>
      </MobileLayout>
    );
  }

  if (error || !member) {
    return (
      <MobileLayout showNavigation={false}>
        <div className="p-4 text-destructive">
          {error || "Team member not found"}
        </div>
      </MobileLayout>
    );
  }
  
  return (
    <MobileLayout showNavigation={false}>
      <div className="space-y-3 p-3">
        {/* Header */}
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="text-lg font-bold">Team Member Details</h1>
        </div>

        {/* Profile Card */}
        <Card className="bg-transparent border-none shadow-none mt-0">
          <CardContent className="flex gap-2 mt-0  transition-shadow bg-white border-2 shadow-sm cursor-pointer text-start border-blue-100/50 rounded-xl">
            <div className="relative inline-block">
              <Avatar className="w-16 h-16">
                <AvatarImage
                  src={member.avatar || "/placeholder.svg"}
                  alt={member.name || "User"}
                  className="!rounded-lg"
                />
                <AvatarFallback className="text-2xl font-bold !rounded-lg">
                  {(member?.name?.[0] || "L").toUpperCase()}
                </AvatarFallback>
              </Avatar>
            </div>

            <div className="flex flex-col justify-center">
              <h2 className="text-lg font-bold capitalize">{member.name}</h2>
              {/* <p className="text-xs text-muted-foreground">
                {member.reference || "Not specified"}ggg
              </p> */}
              <p className="text-xs text-muted-foreground">
                Created{" "}
                {member.created_at
                  ? new Date(member.created_at).toLocaleString("en-IN", {
                      timeZone: "Asia/Kolkata",
                      year: "numeric",
                      month: "short",
                      day: "2-digit",
                      hour: "2-digit",
                      minute: "2-digit",
                      second: "2-digit",
                      hour12: true, // optional, shows AM/PM
                    })
                  : "—"}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Contact Info */}
        <Card className="transition-shadow bg-white border-2 shadow-sm border-blue-100/50 rounded-xl">
          <CardHeader className="pt-2 pb-0">
            <CardTitle className="text-base font-bold">
              Contact Information
            </CardTitle>
          </CardHeader>

          <CardContent className="space-y-1">
            <div>
              <Label>Email</Label>
              <Input
                value={member.email || "—"}
                disabled
                className="h-8 text-xs"
              />
            </div>
            <div>
              <Label>Phone</Label>
              <Input
                value={member.contact_number || "—"}
                disabled
                className="h-8 text-xs"
              />
            </div>

            <div>
              <Label>User Author</Label>
              <Input
                value={creatorName || "—"}
                disabled
                className="h-8 text-xs"
              />
            </div>

            {member.location && (
              <div>
                <Label>Location</Label>
                <Input
                  value={member.location || "—"}
                  disabled
                  className="h-8 text-xs"
                />
              </div>
            )}
            {member.website && (
              <div>
                <Label>Website</Label>
                <Input
                  value={member.website || "—"}
                  disabled
                  className="h-8 text-xs"
                />
              </div>
            )}
          </CardContent>
        </Card>

        {/* Actions */}

        {/* Actions */}
        <Card className="transition-shadow bg-white border-2 shadow-sm border-blue-100/50 rounded-xl">
          <CardHeader className="py-2">
            <CardTitle className="text-base font-bold">Actions</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-wrap gap-2">
            <div className="flex items-center gap-2 mb-1">
              {/* Phone */}
              <a
                href={`tel:${member.contact_number}`}
                className="flex items-center justify-center w-12 h-6 rounded-full border border-blue-600 bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors"
                title="Call"
              >
                <Phone size={14} />
              </a>

              {/* Email */}
              <a
                href={`mailto:${member.email}`}
                className="flex items-center justify-center w-12 h-6 rounded-full border border-red-600 bg-red-50 text-red-600 hover:bg-red-100 transition-colors"
                title="Send Email"
              >
                <Mail size={14} />
              </a>
            </div>
          </CardContent>
        </Card>

        {/* Edit & Delete */}
        <div className="flex gap-2">
          <Button
            className="flex-1 h-8 text-xs rounded-full bg-gradient-to-r from-[#00a8b4fa] to-[#00c8d391] text-white "
            onClick={() => {
              setEditOnOpen(true);
              setIsEditModalOpen(true);
            }}
          >
            Edit Team Member
          </Button>
          <Button
            className="flex-1 h-8 text-xs rounded-full bg-red-700/90 text-white "
            onClick={() => setDeleteOpen(true)}
          >
            <Trash /> Delete Team Member
          </Button>
        </div>
      </div>

      <EditTeamMemberModal
        isOpen={isEditModalOpen}
        setIsOpen={(open) => {
          setIsEditModalOpen(open);
          if (!open) setEditOnOpen(false);
        }}
        member={member}
        onUpdated={(updatedMember) => setMember(updatedMember)}
        startEditable={editOnOpen}
      />
      <DeleteTeamMemberModal
        memberId={member?.id}
        open={deleteOpen}
        setOpen={setDeleteOpen}
      />
    </MobileLayout>
  );
}
